/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ConnectConfigTabs.jsx -- R13 Step 4 P1.
 *
 * Lifted + extended from the deleted ConnectEditor.jsx. Panels for:
 *   - Email templates          -> /api/admin/connect/email-templates
 *   - Invite prompts           -> /api/admin/connect/invite-prompts
 *   - Status wording           -> /api/admin/connect/catalog/status-wording
 *   - Pilot programmes         -> /api/admin/connect/catalog/pilot-programmes
 *   - CTAs                     -> /api/admin/connect/catalog/ctas
 *   - Campaigns                -> /api/admin/connect/catalog/campaigns
 *   - Locked panel             (static list)
 *
 * Every tab calls through `adminFetch` (passed from ConnectCatalog) so
 * the auth transport (Bearer + x-2fa-session) matches the catalog
 * tabs exactly -- no new security code paths.
 */

import React, { useCallback, useEffect, useState } from 'react';

// -- Shared constants ------------------------------------------------------

const EMAIL_PLACEHOLDER_HELP = [
  ['greeting',          'Server-rendered entity-aware greeting (LOCKED).'],
  ['owner_name',        'Owner full name, title-cased.'],
  ['owner_title',       'Owner title.'],
  ['owner_company',     'Owner organisation.'],
  ['contact_org',       'Contact\'s organisation.'],
  ['hook',              'Event-aware opening (intro) OR one-line page summary (invite).'],
  ['personal_lines',    'Owner-written personal lines (invite only).'],
  ['roadmap_link',      'Full invite welcome URL (invite only).'],
  ['invite_link',       'Alias for roadmap_link.'],
  ['signature',         '"I\'m X, Title, Company." signature.'],
  ['site_url',          'Company website URL.'],
  ['company_intro',     'Env CONNECT_COMPANY_INTRO.'],
  ['owner_phone_line',  '"Phone: <num>" fragment, empty when owner has no phone.']
];

const LOCKED_ITEMS = [
  { label: 'Opt-out line + Unsubscribe + Remove-my-details anchors',
    detail: 'Code-injected into the branded card footer on every email.' },
  { label: 'Owner vCard QR block + vCard file attachment',
    detail: 'Rendered by services/connect/ownerQr.js + vcard.js.' },
  { label: 'CTA button HTML (invite only)',
    detail: 'Table-based email-safe button; admin edits surrounding body only.' },
  { label: 'Entity-aware greeting logic',
    detail: 'Server renders {{greeting}} via services/connect/greeting.js.' },
  { label: 'Safety scrubs (no invented offerings, no pricing, no quoting notes, no UPI)',
    detail: 'Enforced in inviteRoadmapAdapter._scrub*. Appended after any admin-edited prompt.' },
  { label: 'List-Unsubscribe / List-Unsubscribe-Post headers',
    detail: 'RFC 8058. Set by the sender, not the template.' },
  { label: 'Honesty check on status wording',
    detail: 'statusWordingValidator refuses "available now"/dates on coming_soon or on_request wording.' },
  { label: 'Rate limits + Turnstile on feedback submissions',
    detail: '10/15min per IP + 3/hr per invite token; Turnstile verified server-side.' }
];

// -- Styles ----------------------------------------------------------------

const styles = {
  section: { marginBottom: 24 },
  card: { background: 'white', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(15,23,42,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 8px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600 },
  td: { padding: '10px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  textarea: { width: '100%', minHeight: 180, padding: 12, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 13, lineHeight: 1.5 },
  input: { width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 },
  btn: (variant) => ({
    padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
    background: variant === 'primary' ? '#06b6d4' : variant === 'danger' ? '#dc2626' : '#64748b',
    color: 'white', marginRight: 8
  }),
  pill: (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: color, color: 'white', fontSize: 11, fontWeight: 700 }),
  errorBox: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 6, marginTop: 8 },
  okBox:    { background: '#f0fdf4', color: '#166534', padding: 10, borderRadius: 6, marginTop: 8 },
  previewPane: { background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 6, maxHeight: 500, overflow: 'auto', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 12, whiteSpace: 'pre-wrap' }
};

// ===== Email Templates =====================================================

export function EmailTemplatesTab({ adminFetch }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sampleKind, setSampleKind] = useState('company');

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/email-templates');
      setRows(r.data || []);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (promptId) => {
    setErr(null); setPreview(null);
    const r = await adminFetch(`/api/admin/connect/email-templates/${encodeURIComponent(promptId)}`);
    setSel({ promptId, row: r.data });
    setDraft((r.data && r.data.promptText) || '');
  };

  const save = async () => {
    if (!sel) return;
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/api/admin/connect/email-templates/${encodeURIComponent(sel.promptId)}`, {
        method: 'PUT', body: JSON.stringify({ promptText: draft, changeDescription: 'Admin edit' })
      });
      await load(); await openEdit(sel.promptId);
    } catch (e) {
      setErr(e.body && e.body.errors ? e.body.errors.join(', ') : e.message);
    } finally { setBusy(false); }
  };

  const previewRender = async () => {
    if (!sel) return;
    setBusy(true); setErr(null); setPreview(null);
    try {
      const SAMPLES = {
        individual: { contact: { full_name: 'Priya Mehta', represents: 'individual' } },
        company:    { contact: { full_name: 'Priya Mehta', organisation: 'Zharokha Lifestyle', represents: 'company' } },
        institute:  { contact: { full_name: 'Dr Neha Kapoor', organisation: 'Northwind Institute', role: 'Professor', represents: 'institute' } }
      };
      const sample = Object.assign({}, SAMPLES[sampleKind] || SAMPLES.company, {
        owner: { fullName: 'Lalit Ratwani', title: 'Founder', organisation: 'SkillDelivery',
                 phone: '+91 9999988888', email: 'lalit@skilldelivery.co.in', website: 'https://skilldelivery.co.in' },
        event: { name: 'Expo 2026' }
      });
      const r = await adminFetch(
        `/api/admin/connect/email-templates/${encodeURIComponent(sel.promptId)}/preview`,
        { method: 'POST', body: JSON.stringify({ sample }) }
      );
      setPreview(r.data || null);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const promote = async (toVersion) => {
    if (!sel) return;
    setBusy(true);
    try {
      await adminFetch(
        `/api/admin/connect/email-templates/${encodeURIComponent(sel.promptId)}/promote`,
        { method: 'POST', body: JSON.stringify({ toVersion }) }
      );
      await openEdit(sel.promptId);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // R14 Round 2 fix 2 -- intro-card variant preview. Content lives in
  // Page text, not here; the preview endpoint renders the full HTML
  // with a sample fixture so the admin can screenshot it.
  const [introCardPreview, setIntroCardPreview] = useState(null);
  const openIntroCardPreview = async () => {
    setErr(null);
    try {
      const r = await adminFetch('/api/admin/connect/email-templates/intro-card/preview');
      setIntroCardPreview(r.data || null);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Email templates ({rows.length})</h3>
        <table style={styles.table}>
          <thead><tr><th style={styles.th}>Prompt id</th><th style={styles.th}>Version</th><th style={styles.th}>Last edit</th><th style={styles.th}>Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.promptId}>
                <td style={styles.td}><code>{r.promptId}</code></td>
                <td style={styles.td}>{(r.row && r.row.version) ?? <em>not seeded</em>}</td>
                <td style={styles.td}>{r.row && r.row.updatedAt ? new Date(r.row.updatedAt).toLocaleString() : '—'}</td>
                <td style={styles.td}><button style={styles.btn('primary')} onClick={() => openEdit(r.promptId)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* R14 Round 2 fix 2 -- Intro-card email preview tile.
          Content lives in Page text; the preview endpoint renders the
          full HTML with a sample fixture. Side-by-side iframes at
          600px (desktop email) and 360px (narrow mobile).  */}
      <div style={{ ...styles.card, marginTop: 16 }}>
        <h4 style={{ marginTop: 0 }}>Intro-card email (R14) -- preview</h4>
        <p style={{ color: '#64748b', fontSize: 13 }}>
          Content is admin-editable via the <strong>Page text</strong> tab under keys <code>intro_card.*</code>.
          Preview below renders with the committed defaults + any Page-text overrides.
        </p>
        <button type="button" style={styles.btn('primary')} onClick={openIntroCardPreview}>
          Render intro-card preview
        </button>
        {introCardPreview && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
              Subject: <strong>{introCardPreview.subject}</strong>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Desktop 600px</div>
                <iframe
                  title="intro-card-desktop"
                  srcDoc={introCardPreview.html}
                  style={{ width: 600, height: 900, border: '1px solid #e2e8f0', background: 'white' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Mobile 360px</div>
                <iframe
                  title="intro-card-mobile"
                  srcDoc={introCardPreview.html}
                  style={{ width: 360, height: 900, border: '1px solid #e2e8f0', background: 'white' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {sel && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>Editing <code>{sel.promptId}</code></h4>
          <details style={{ marginBottom: 10 }}>
            <summary style={{ cursor: 'pointer', color: '#0e7490' }}>Placeholder help</summary>
            <ul style={{ fontSize: 13, lineHeight: 1.6, color: '#475569' }}>
              {EMAIL_PLACEHOLDER_HELP.map(([k, v]) => (
                <li key={k}><code>{`{{${k}}}`}</code> — {v}</li>
              ))}
            </ul>
          </details>
          <textarea style={styles.textarea} value={draft} onChange={(e) => setDraft(e.target.value)} disabled={busy} />
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center' }}>
            <button style={styles.btn('primary')} disabled={busy} onClick={save}>Save new version</button>
            <select value={sampleKind} onChange={(e) => setSampleKind(e.target.value)} style={{ padding: 6, marginRight: 8 }}>
              <option value="individual">Sample: individual</option>
              <option value="company">Sample: company</option>
              <option value="institute">Sample: institute (Prof)</option>
            </select>
            <button style={styles.btn('')} disabled={busy} onClick={previewRender}>Preview render</button>
          </div>
          {err && <div style={styles.errorBox}>{err}</div>}
          {sel.row && sel.row.changeLog && sel.row.changeLog.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h5>Previous versions</h5>
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>v</th><th style={styles.th}>By</th><th style={styles.th}>When</th><th style={styles.th}>Note</th><th style={styles.th}>Action</th></tr></thead>
                <tbody>
                  {sel.row.changeLog.slice().reverse().map((c, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{c.version}</td>
                      <td style={styles.td}>{c.changedBy}</td>
                      <td style={styles.td}>{c.changedAt ? new Date(c.changedAt).toLocaleString() : '—'}</td>
                      <td style={styles.td}>{c.changeDescription || '—'}</td>
                      <td style={styles.td}><button style={styles.btn('')} onClick={() => promote(c.version)} disabled={busy}>Rollback</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview && (
            <div style={{ marginTop: 16 }}>
              <h5>Preview — HTML</h5>
              <div style={{ ...styles.previewPane, background: 'white', color: '#0f172a' }}
                   dangerouslySetInnerHTML={{ __html: preview.html }} />
              <h5>Preview — plain text</h5>
              <div style={styles.previewPane}>{preview.text}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Email Catalog v2 (R15) ============================================
//
// Backend: /api/admin/connect/email-catalog/*
//   GET  /list                                        -> all keys + active
//   GET  /:kind/:variant/:format/:lang/versions       -> version history
//   PUT  /:kind/:variant/:format/:lang                -> save new version
//   POST /:kind/:variant/:format/:lang/preview        -> render w/ contact
//   POST /:kind/:variant/:format/:lang/send-test      -> admin inbox send
//   POST /:kind/:variant/:format/:lang/activate       -> promote version
//
// Gated by CONNECT_EMAIL_CATALOG_ENABLED (default ON after R15 next).
// When OFF server returns 503; this tab reads it and renders a hint.

export function EmailCatalogTab({ adminFetch }) {
  const [list, setList] = useState([]);
  const [key, setKey] = useState(null); // { kind, variant, format, lang }
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [contactId, setContactId] = useState('');
  const [preview, setPreview] = useState(null);
  const [sendTo, setSendTo] = useState('');
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await adminFetch('/api/admin/connect/email-catalog/list');
      // R15 follow-up (2026-10-11) -- admin surface now 503s with
      // `CATALOG_ADMIN_DISABLED` (own flag, default ON). Keep the old
      // code recognised for back-compat during the rollout.
      if (r && r.success === false
          && (r.code === 'CATALOG_ADMIN_DISABLED' || r.code === 'CATALOG_DISABLED')) {
        setDisabled(true); setList([]); return;
      }
      setDisabled(false);
      setList((r && r.data && r.data.templates) || []);
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  }, [adminFetch]);

  const loadVersions = useCallback(async (k) => {
    setBusy(true); setErr(null); setPreview(null);
    try {
      const url = `/api/admin/connect/email-catalog/${k.kind}/${k.variant}/${k.format}/${k.lang}/versions`;
      const r = await adminFetch(url);
      setVersions((r && r.data && r.data.versions) || []);
      setDraft('');       // body_preview is TRUNCATED -- do NOT preload.
      setDraftNote('');
      setEditing(false);
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  }, [adminFetch]);

  // R15 next review (2026-10-11) -- editor fetches the FULL body
  // before opening the textarea. The list view's body_preview is
  // intentionally truncated at 400 chars; opening Edit on it would
  // silently save a cropped template over the active row.
  const openEditor = useCallback(async (version) => {
    if (!key) return;
    setErr(null); setOk(null); setBusy(true);
    try {
      const url = `/api/admin/connect/email-catalog/${key.kind}/${key.variant}/${key.format}/${key.lang}/version/${version}`;
      const r = await adminFetch(url);
      if (r && r.success && r.data && r.data.template) {
        setDraft(r.data.template.body || '');
        setDraftNote('');
        setEditing(true);
      } else {
        setErr((r && r.error) || 'Could not load the full body');
      }
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  }, [adminFetch, key]);

  useEffect(() => { loadList(); }, [loadList]);

  const select = (k) => { setKey(k); loadVersions(k); };

  const save = async () => {
    if (!key) return;
    setErr(null); setOk(null); setBusy(true);
    try {
      const url = `/api/admin/connect/email-catalog/${key.kind}/${key.variant}/${key.format}/${key.lang}`;
      const r = await adminFetch(url, {
        method: 'PUT',
        body: JSON.stringify({ body: draft, change_note: draftNote })
      });
      if (r && r.success) {
        setOk(`Saved as v${r.data.version} (inactive). Click Activate to promote.`);
        setEditing(false);
        await loadVersions(key);
      } else {
        setErr((r && r.error) || 'Save failed');
      }
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  };

  const activate = async (version) => {
    if (!key) return;
    setErr(null); setOk(null); setBusy(true);
    try {
      const url = `/api/admin/connect/email-catalog/${key.kind}/${key.variant}/${key.format}/${key.lang}/activate`;
      const r = await adminFetch(url, {
        method: 'POST',
        body: JSON.stringify({ version })
      });
      if (r && r.success) {
        setOk(`v${version} is now active.`);
        await loadVersions(key);
        await loadList();
      } else {
        setErr((r && r.error) || 'Activate failed');
      }
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!key || !contactId) {
      setErr('Enter a contact_id first.'); return;
    }
    setErr(null); setOk(null); setBusy(true);
    try {
      const url = `/api/admin/connect/email-catalog/${key.kind}/${key.variant}/${key.format}/${key.lang}/preview`;
      const r = await adminFetch(url, {
        method: 'POST',
        body: JSON.stringify({ contact_id: contactId })
      });
      if (r && r.success) setPreview(r.data);
      else setErr((r && r.error) || 'Preview failed');
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!key || !contactId || !sendTo) {
      setErr('Enter contact_id + send-to email first.'); return;
    }
    setErr(null); setOk(null); setBusy(true);
    try {
      const url = `/api/admin/connect/email-catalog/${key.kind}/${key.variant}/${key.format}/${key.lang}/send-test`;
      const r = await adminFetch(url, {
        method: 'POST',
        body: JSON.stringify({ contact_id: contactId, to: sendTo })
      });
      if (r && r.success) setOk(`Test send queued (messageId: ${r.data.messageId}).`);
      else setErr((r && r.error) || 'Send failed');
    } catch (e) {
      setErr(e && e.message || String(e));
    } finally { setBusy(false); }
  };

  if (disabled) {
    return (
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Email catalog (v2)</h3>
        <p style={{ color: '#64748b', margin: 0 }}>
          Admin surface disabled on this environment. Set
          <code> CONNECT_EMAIL_CATALOG_ADMIN_ENABLED=true</code> on Render to open the catalog
          (independent of <code>CONNECT_EMAIL_CATALOG_ENABLED</code>, which gates the real
          intro send).
        </p>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={{ marginTop: 0 }}>Email catalog (v2) ({list.length})</h3>
      {err && <div style={styles.errorBox}>{err}</div>}
      {ok  && <div style={styles.okBox}>{ok}</div>}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Key</th>
            <th style={styles.th}>Active</th>
            <th style={styles.th}>Versions</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {list.map((t) => (
            <tr key={`${t.kind}.${t.variant}.${t.format}.${t.lang}`}>
              <td style={styles.td}>
                <code>{t.kind}.{t.variant}.{t.format}.{t.lang}</code>
              </td>
              <td style={styles.td}>v{t.active_version || '-'}</td>
              <td style={styles.td}>{t.versions}</td>
              <td style={styles.td}>
                <button style={styles.btn('primary')} disabled={busy}
                        onClick={() => select({ kind: t.kind, variant: t.variant, format: t.format, lang: t.lang })}>
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {key && (
        <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
          <h4 style={{ margin: '0 0 10px' }}>
            {key.kind}.{key.variant}.{key.format}.{key.lang}
          </h4>
          <div style={{ marginBottom: 10 }}>
            {editing ? (
              <>
                <textarea style={styles.textarea}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          placeholder="Template body (use {{var}} / {{block:name}}...{{/block}})" />
                <input style={styles.input} placeholder="Change note (optional)"
                       value={draftNote} onChange={(e) => setDraftNote(e.target.value)} />
                <div style={{ marginTop: 8 }}>
                  <button style={styles.btn('primary')} disabled={busy} onClick={save}>Save as new version</button>
                  <button style={styles.btn()} disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                </div>
                <p style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>
                  Save lands as inactive. Promote via Activate after a Preview round.
                </p>
              </>
            ) : (
              <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>
                Click <strong>Edit</strong> on a version below to open the full body in the editor.
                The list view's body text is truncated at 400 chars; the editor fetches the full
                body so no edit saves a cropped template.
              </p>
            )}
          </div>
          <h5 style={{ margin: '10px 0 6px' }}>Versions</h5>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>v</th>
                <th style={styles.th}>Active</th>
                <th style={styles.th}>By</th>
                <th style={styles.th}>When</th>
                <th style={styles.th}>Note</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td style={styles.td}>v{v.version}</td>
                  <td style={styles.td}>{v.active ? <span style={styles.pill('#16a34a')}>ACTIVE</span> : '-'}</td>
                  <td style={styles.td}>{v.created_by || '-'}</td>
                  <td style={styles.td}>{new Date(v.created_at).toLocaleString()}</td>
                  <td style={styles.td} title={v.change_note || ''}>
                    {v.change_note ? String(v.change_note).slice(0, 60) : '-'}
                  </td>
                  <td style={styles.td}>
                    <button style={styles.btn()} disabled={busy}
                            onClick={() => openEditor(v.version)}>Edit</button>
                    {!v.active && (
                      <button style={styles.btn('primary')} disabled={busy}
                              onClick={() => activate(v.version)}>Activate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
            <h5 style={{ margin: '0 0 6px' }}>Preview / send-test</h5>
            <input style={styles.input} placeholder="contact_id (UUID)"
                   value={contactId} onChange={(e) => setContactId(e.target.value)} />
            <div style={{ marginTop: 6 }}>
              <button style={styles.btn('primary')} disabled={busy} onClick={doPreview}>Preview</button>
            </div>
            {preview && (
              <div style={{ marginTop: 10 }}>
                <div style={{ marginBottom: 6 }}><strong>Subject:</strong> {preview.subject}</div>
                {/*
                  R15 next review (2026-10-11) -- HTML preview renders
                  inside a sandboxed iframe (sandbox="" blocks scripts,
                  forms, same-origin access, popups, and top-nav).
                  srcDoc is set from preview.html as-rendered by the
                  server; the admin sees the real layout instead of
                  escaped HTML in a <div>.
                */}
                <iframe
                  title={`preview-${preview.templateKey || 'intro'}`}
                  sandbox=""
                  srcDoc={preview.html}
                  style={{ width: '100%', height: 500, border: '1px solid #e2e8f0', borderRadius: 6, background: 'white' }}
                />
                <div style={{ marginTop: 8 }}><strong>Text:</strong></div>
                <div style={styles.previewPane}>{preview.text}</div>
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <input style={styles.input} placeholder="Send-test to (your email)"
                     value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
              <div style={{ marginTop: 6 }}>
                <button style={styles.btn()} disabled={busy} onClick={sendTest}>Send test</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Invite Prompts ======================================================

export function InvitePromptsTab({ adminFetch }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assembled, setAssembled] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/invite-prompts');
      setRows(r.data || []);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (promptId) => {
    setErr(null); setAssembled(null);
    const r = await adminFetch(`/api/admin/connect/invite-prompts/${encodeURIComponent(promptId)}`);
    setSel({ promptId, row: r.data });
    setDraft((r.data && r.data.promptText) || '');
  };

  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await adminFetch(`/api/admin/connect/invite-prompts/${encodeURIComponent(sel.promptId)}`, {
        method: 'PUT', body: JSON.stringify({ promptText: draft })
      });
      await load(); await openEdit(sel.promptId);
    } catch (e) { setErr(e.body && e.body.errors ? e.body.errors.join(', ') : e.message); }
    finally { setBusy(false); }
  };

  const previewAssembled = async () => {
    setBusy(true); setErr(null); setAssembled(null);
    try {
      const r = await adminFetch('/api/admin/connect/invite-prompts/_preview-assembled',
        { method: 'POST', body: JSON.stringify({}) });
      setAssembled(r.data || null);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Invite prompts ({rows.length})</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Editable sub-blocks of the invite-roadmap safety prelude. The LOCKED safety
          rules (invented-offering scrub, pricing block, UPI block, live-catalog grounding)
          are always appended after your text.
        </p>
        <table style={styles.table}>
          <thead><tr><th style={styles.th}>Prompt id</th><th style={styles.th}>Version</th><th style={styles.th}>Last edit</th><th style={styles.th}>Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.promptId}>
                <td style={styles.td}><code>{r.promptId}</code></td>
                <td style={styles.td}>{(r.row && r.row.version) ?? <em>not seeded</em>}</td>
                <td style={styles.td}>{r.row && r.row.updatedAt ? new Date(r.row.updatedAt).toLocaleString() : '—'}</td>
                <td style={styles.td}><button style={styles.btn('primary')} onClick={() => openEdit(r.promptId)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 12 }}>
          <button style={styles.btn('')} onClick={previewAssembled} disabled={busy}>Preview assembled prelude (sample contact)</button>
        </div>
      </div>
      {sel && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>Editing <code>{sel.promptId}</code></h4>
          <textarea style={styles.textarea} value={draft} onChange={(e) => setDraft(e.target.value)} disabled={busy} />
          <div style={{ marginTop: 10 }}>
            <button style={styles.btn('primary')} disabled={busy} onClick={save}>Save new version</button>
          </div>
          {err && <div style={styles.errorBox}>{err}</div>}
        </div>
      )}
      {assembled && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>Assembled prelude (sample Zharokha)</h4>
          <ul style={{ fontSize: 13, color: '#475569' }}>
            {(assembled.lockedSegments || []).map((seg) => (
              <li key={seg}><span style={styles.pill('#dc2626')}>Locked</span> {seg}</li>
            ))}
          </ul>
          <div style={styles.previewPane}>{assembled.preludeText}</div>
        </div>
      )}
    </div>
  );
}

// ===== Status Wording ======================================================

export function StatusWordingTab({ adminFetch }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/catalog/status-wording');
      setRows(r.data || []);
      const next = {};
      for (const row of (r.data || [])) next[row.key] = row.text;
      setEdit(next);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setBusy(true); setErr(null);
    try {
      await adminFetch(`/api/admin/connect/catalog/status-wording/${encodeURIComponent(key)}`, {
        method: 'PATCH', body: JSON.stringify({ text: edit[key] || '' })
      });
      await load();
    } catch (e) {
      const msg = e.body && e.body.errors ? e.body.errors.join(', ') : (e.body && e.body.error) || e.message;
      setErr(`${key}: ${msg}`);
    } finally { setBusy(false); }
  };

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Status wording</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          The invite drafter + invite page read these. Honesty check (locked) refuses
          "available now" / dates on <code>coming_soon</code> or <code>on_request</code>.
        </p>
        {err && <div style={styles.errorBox}>{err}</div>}
        <table style={styles.table}>
          <thead><tr><th style={styles.th}>Key</th><th style={styles.th}>Text</th><th style={styles.th}>Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={styles.td}><strong>{r.key}</strong></td>
                <td style={styles.td}>
                  <input style={styles.input} value={edit[r.key] || ''}
                         onChange={(e) => setEdit({ ...edit, [r.key]: e.target.value })} />
                </td>
                <td style={styles.td}>
                  <button style={styles.btn('primary')} disabled={busy} onClick={() => save(r.key)}>Save</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== Pilot Programmes ====================================================

export function PilotProgrammesTab({ adminFetch, options }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/catalog/pilot-programmes');
      setRows(r.data || []);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!sel) return;
    setBusy(true); setErr(null);
    try {
      const body = JSON.stringify(sel);
      if (sel.id) {
        await adminFetch(`/api/admin/connect/catalog/pilot-programmes/${encodeURIComponent(sel.id)}`,
          { method: 'PATCH', body });
      } else {
        await adminFetch('/api/admin/connect/catalog/pilot-programmes',
          { method: 'POST', body });
      }
      setSel(null); await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete pilot programme?')) return;
    try {
      await adminFetch(`/api/admin/connect/catalog/pilot-programmes/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); }
  };

  const toCsv = (arr) => Array.isArray(arr) ? arr.join(',') : '';
  const fromCsv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Pilot programmes ({rows.length})</h3>
        {err && <div style={styles.errorBox}>{err}</div>}
        <button style={styles.btn('primary')}
                onClick={() => setSel({ name: '', status: 'closed', offering_ids: [] })}>
          + New programme
        </button>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Name</th><th style={styles.th}>Status</th>
            <th style={styles.th}>Duration</th><th style={styles.th}>Interest</th>
            <th style={styles.th}>Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}><strong>{r.name}</strong></td>
                <td style={styles.td}>{r.status}</td>
                <td style={styles.td}>{r.duration || '—'}</td>
                <td style={styles.td}>{r.interest_count || 0}</td>
                <td style={styles.td}>
                  <button style={styles.btn('')} onClick={() => setSel(r)}>Edit</button>
                  <button style={styles.btn('danger')} onClick={() => del(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>{sel.id ? `Edit ${sel.name}` : 'New programme'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>Name <input style={styles.input} value={sel.name || ''}
              onChange={(e) => setSel({ ...sel, name: e.target.value })} /></label>
            <label>Status
              <select style={styles.input} value={sel.status || 'closed'}
                onChange={(e) => setSel({ ...sel, status: e.target.value })}>
                {(options.programme_statuses || ['open','closed']).map((s) =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Duration <input style={styles.input} value={sel.duration || ''}
              onChange={(e) => setSel({ ...sel, duration: e.target.value })} /></label>
            <label>Price text <input style={styles.input} value={sel.price_text || ''}
              onChange={(e) => setSel({ ...sel, price_text: e.target.value })} /></label>
            <label>Max partners <input type="number" style={styles.input}
              value={sel.max_partners == null ? '' : sel.max_partners}
              onChange={(e) => setSel({ ...sel, max_partners: e.target.value === '' ? null : Number(e.target.value) })} /></label>
            <label>Link URL <input style={styles.input} value={sel.link_url || ''}
              onChange={(e) => setSel({ ...sel, link_url: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Offering IDs (CSV)
              <input style={styles.input} value={toCsv(sel.offering_ids)}
                onChange={(e) => setSel({ ...sel, offering_ids: fromCsv(e.target.value) })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Benefits
              <textarea style={styles.textarea} value={sel.benefits || ''}
                onChange={(e) => setSel({ ...sel, benefits: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Requirements
              <textarea style={styles.textarea} value={sel.requirements || ''}
                onChange={(e) => setSel({ ...sel, requirements: e.target.value })} /></label>
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={styles.btn('primary')} onClick={save} disabled={busy}>Save</button>
            <button style={styles.btn('')} onClick={() => setSel(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== CTAs ================================================================

export function CtasTab({ adminFetch, options }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/catalog/ctas');
      setRows(r.data || []);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!sel) return;
    setBusy(true); setErr(null);
    try {
      const body = JSON.stringify(sel);
      if (sel.id) {
        await adminFetch(`/api/admin/connect/catalog/ctas/${encodeURIComponent(sel.id)}`,
          { method: 'PATCH', body });
      } else {
        await adminFetch('/api/admin/connect/catalog/ctas', { method: 'POST', body });
      }
      setSel(null); await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete CTA?')) return;
    try {
      await adminFetch(`/api/admin/connect/catalog/ctas/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); }
  };

  const toCsv = (arr) => Array.isArray(arr) ? arr.join(',') : '';
  const fromCsv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>CTAs ({rows.length})</h3>
        {err && <div style={styles.errorBox}>{err}</div>}
        <button style={styles.btn('primary')}
                onClick={() => setSel({ label: '', action: 'pilot_interest', visible_for_statuses: [], is_active: true })}>
          + New CTA
        </button>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Label</th><th style={styles.th}>Action</th>
            <th style={styles.th}>Visible for statuses</th><th style={styles.th}>Active?</th>
            <th style={styles.th}>Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}><strong>{r.label}</strong></td>
                <td style={styles.td}><code>{r.action}</code></td>
                <td style={styles.td}>{(r.visible_for_statuses || []).join(', ') || '— all —'}</td>
                <td style={styles.td}>{r.is_active ? 'Yes' : 'No'}</td>
                <td style={styles.td}>
                  <button style={styles.btn('')} onClick={() => setSel(r)}>Edit</button>
                  <button style={styles.btn('danger')} onClick={() => del(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>{sel.id ? `Edit ${sel.label}` : 'New CTA'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>Label <input style={styles.input} value={sel.label || ''}
              onChange={(e) => setSel({ ...sel, label: e.target.value })} /></label>
            <label>Action
              <select style={styles.input} value={sel.action || 'pilot_interest'}
                onChange={(e) => setSel({ ...sel, action: e.target.value })}>
                {(options.cta_actions || ['pilot_interest','request_call','send_details','link']).map((a) =>
                  <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label>Link URL (required when action=link) <input style={styles.input} value={sel.link_url || ''}
              onChange={(e) => setSel({ ...sel, link_url: e.target.value })} /></label>
            <label>Display order <input type="number" style={styles.input} value={sel.display_order || 100}
              onChange={(e) => setSel({ ...sel, display_order: Number(e.target.value) || 100 })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Visible for statuses (CSV; empty = all)
              <input style={styles.input} value={toCsv(sel.visible_for_statuses)}
                onChange={(e) => setSel({ ...sel, visible_for_statuses: fromCsv(e.target.value) })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Visible for offerings (UUID CSV; empty = any)
              <input style={styles.input} value={toCsv(sel.visible_for_offerings)}
                onChange={(e) => setSel({ ...sel, visible_for_offerings: fromCsv(e.target.value) })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>
              <input type="checkbox" checked={sel.is_active !== false}
                onChange={(e) => setSel({ ...sel, is_active: e.target.checked })} /> Active
            </label>
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={styles.btn('primary')} onClick={save} disabled={busy}>Save</button>
            <button style={styles.btn('')} onClick={() => setSel(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Campaigns ===========================================================

export function CampaignsTab({ adminFetch, options }) {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/catalog/campaigns');
      setRows(r.data || []);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!sel) return;
    setBusy(true); setErr(null);
    try {
      const body = JSON.stringify(sel);
      if (sel.id) {
        await adminFetch(`/api/admin/connect/catalog/campaigns/${encodeURIComponent(sel.id)}`,
          { method: 'PATCH', body });
      } else {
        await adminFetch('/api/admin/connect/catalog/campaigns',
          { method: 'POST', body });
      }
      setSel(null); await load();
    } catch (e) {
      setErr(e.body && e.body.errors ? e.body.errors.join(', ') : e.message);
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete campaign?')) return;
    try {
      await adminFetch(`/api/admin/connect/catalog/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Campaigns ({rows.length})</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Store + preview only; bulk sending ships in a later release.
        </p>
        {err && <div style={styles.errorBox}>{err}</div>}
        <button style={styles.btn('primary')} onClick={() => setSel({ name: '', status: 'draft', target_tags: {} })}>
          + New campaign
        </button>
        <table style={styles.table}>
          <thead><tr><th style={styles.th}>Name</th><th style={styles.th}>Status</th><th style={styles.th}>Target</th><th style={styles.th}>Action</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}><strong>{r.name}</strong></td>
                <td style={styles.td}>{r.status}</td>
                <td style={styles.td}><code style={{ fontSize: 11 }}>{JSON.stringify(r.target_tags || {})}</code></td>
                <td style={styles.td}>
                  <button style={styles.btn('')} onClick={() => setSel(r)}>Edit</button>
                  <button style={styles.btn('danger')} onClick={() => del(r.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && (
        <div style={{ ...styles.card, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>{sel.id ? `Edit ${sel.name}` : 'New campaign'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label>Name <input style={styles.input} value={sel.name || ''}
              onChange={(e) => setSel({ ...sel, name: e.target.value })} /></label>
            <label>Status
              <select style={styles.input} value={sel.status || 'draft'}
                onChange={(e) => setSel({ ...sel, status: e.target.value })}>
                {(options.campaign_statuses || ['draft','ready','archived']).map((s) =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label style={{ gridColumn: '1 / 3' }}>Target tags (JSON)
              <textarea style={{ ...styles.textarea, minHeight: 80 }}
                value={JSON.stringify(sel.target_tags || {}, null, 2)}
                onChange={(e) => {
                  try { setSel({ ...sel, target_tags: JSON.parse(e.target.value) }); }
                  catch (_) {/* ignore until valid */}
                }} />
            </label>
            <label style={{ gridColumn: '1 / 3' }}>Email subject <input style={styles.input} value={sel.email_subject || ''}
              onChange={(e) => setSel({ ...sel, email_subject: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Email body
              <textarea style={styles.textarea} value={sel.email_body || ''}
                onChange={(e) => setSel({ ...sel, email_body: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>WhatsApp text
              <textarea style={{ ...styles.textarea, minHeight: 80 }} value={sel.wa_text || ''}
                onChange={(e) => setSel({ ...sel, wa_text: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>LinkedIn text
              <textarea style={{ ...styles.textarea, minHeight: 80 }} value={sel.linkedin_text || ''}
                onChange={(e) => setSel({ ...sel, linkedin_text: e.target.value })} /></label>
            <label style={{ gridColumn: '1 / 3' }}>Landing-page text
              <textarea style={styles.textarea} value={sel.landing_text || ''}
                onChange={(e) => setSel({ ...sel, landing_text: e.target.value })} /></label>
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={styles.btn('primary')} onClick={save} disabled={busy}>Save</button>
            <button style={styles.btn('')} onClick={() => setSel(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Locked panel ========================================================

// ─── R13 Step 7 (task 5) -- Page text tab ────────────────────────────────
//
// Writes connect_pitch_content via /api/admin/connect/catalog/page-text.
// Only the labels with no other home live here: textarea prompts, the
// thank-you line, reaction chips, closing note, captcha hint, PRIVACY
// labels (wrong_person + not_interested -- locked in FE, admin-editable
// labels). Quick-action buttons / pilot CTAs do NOT live here -- those
// come from connect_ctas + connect_pilot_programmes.
//
// English only in this release; the (key, lang) uniqueness means
// adding another locale later is additive.
export function PageTextTab({ adminFetch }) {
  const [rows, setRows] = React.useState([]);
  const [edit, setEdit] = React.useState({});
  const [err, setErr]   = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [ok, setOk]     = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await adminFetch('/api/admin/connect/catalog/page-text');
      setRows(r.data || []);
      const next = {};
      for (const row of (r.data || [])) {
        next[row.key] = row.value != null ? row.value : (row.default_value || '');
      }
      setEdit(next);
    } catch (e) { setErr(e.message); }
  }, [adminFetch]);
  React.useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setBusy(true); setErr(null); setOk(null);
    try {
      await adminFetch(`/api/admin/connect/catalog/page-text/${encodeURIComponent(key)}`, {
        method: 'PATCH', body: JSON.stringify({ value: edit[key] || '' })
      });
      setOk(`${key} saved`);
      await load();
    } catch (e) {
      const msg = (e.body && e.body.error) || e.message;
      setErr(`${key}: ${msg}`);
    } finally { setBusy(false); }
  };

  // Group keys by their first dotted segment so the admin UI reads
  // naturally: "Invite welcome -> reaction", "Invite welcome ->
  // privacy", etc.
  const groups = {};
  for (const r of rows) {
    const parts = r.key.split('.');
    const group = parts.slice(0, 2).join('.'); // e.g. invite_welcome.reaction
    if (!groups[group]) groups[group] = [];
    groups[group].push(r);
  }

  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Page text</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Labels on the invite welcome page. English only in this release.
          "Not interested" / "Not the right person" are LOCKED on the page -- their LABELS live here.
          Quick-action buttons and pilot CTAs are NOT here -- those come from CTAs / Pilot programmes.
        </p>
        {err && <div style={styles.errorBox}>{err}</div>}
        {ok  && <div style={styles.okBox}>{ok}</div>}
        {Object.keys(groups).sort().map((g) => (
          <div key={g} style={{ marginTop: 18 }}>
            <h4 style={{ margin: '0 0 8px', color: '#334155' }}>{g}</h4>
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}>Key</th>
                <th style={styles.th}>Value</th>
                <th style={styles.th}>Default</th>
                <th style={styles.th}>Action</th>
              </tr></thead>
              <tbody>
                {groups[g].map((r) => (
                  <tr key={r.key}>
                    <td style={styles.td}><code style={{ fontSize: 12 }}>{r.key}</code></td>
                    <td style={styles.td}>
                      <input
                        style={styles.input}
                        value={edit[r.key] || ''}
                        onChange={(e) => setEdit({ ...edit, [r.key]: e.target.value })}
                      />
                    </td>
                    <td style={{ ...styles.td, color: '#94a3b8', fontSize: 12 }}>
                      {r.default_value}
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.btn('primary')}
                        disabled={busy || !(edit[r.key] || '').trim()}
                        onClick={() => save(r.key)}
                      >Save</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LockedPanel() {
  return (
    <div style={styles.section}>
      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Locked by SkillDelivery — not editable</h3>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          These pieces of the Connect pipeline are always applied by code. An admin edit cannot remove them.
        </p>
        <ul style={{ lineHeight: 1.8 }}>
          {LOCKED_ITEMS.map((it) => (
            <li key={it.label}>
              <span style={styles.pill('#dc2626')}>Locked</span>{' '}
              <strong>{it.label}</strong>
              <div style={{ color: '#64748b', fontSize: 13, marginLeft: 48 }}>{it.detail}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
